import { ColumnDef, Header, LoadingSpinner, OnChangeFn, PaginationState, Table, Text } from '@psyomics/ui-components';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelReferralRequestDialog } from './cancel-referral-request-dialog';
import { useCancelReferralRequestDialog } from './hooks/use-cancel-referral-request-dialog';
import { useProcessingReferralRequests } from './hooks/use-processing-referral-requests';
import { useProcessingReferralRows } from './hooks/use-processing-referral-rows';
import { useSubmittedReferrals } from './hooks/use-submitted-referrals';
import { SubmittedReferralInvites } from './submitted-referral-invites';
import { isProcessingReferralRow, ProcessingReferralRow, SubmittedReferralRow } from './types';
import { useTableColumns } from './use-table-columns';

type SubmittedReferralsTableProps = {
  readonly columns: ColumnDef<SubmittedReferralRow>[];
  readonly error?: unknown;
  readonly fetching: boolean;
  readonly onPaginationChange: OnChangeFn<PaginationState>;
  readonly pagination: PaginationState;
  readonly rows: SubmittedReferralRow[];
  readonly totalCount: number;
};

const ProcessingReferralMessage = ({ row }: { readonly row: ProcessingReferralRow }) => {
  const { t } = useTranslation();

  return (
    <Text>
      {t(
        row.delayed
          ? 'page.professionalReferral.home.invites.delayed'
          : 'page.professionalReferral.home.invites.preparing'
      )}
    </Text>
  );
};

const SubmittedReferralsTable = ({
  columns,
  error,
  fetching,
  onPaginationChange,
  pagination,
  rows,
  totalCount,
}: SubmittedReferralsTableProps) => {
  const { t } = useTranslation();

  if (fetching && rows.length === 0) return <LoadingSpinner />;

  if (error) return <Text className="mt-4">{t('page.professionalReferral.home.error')}</Text>;

  return (
    <Table
      className="mt-4"
      columns={columns}
      data={rows}
      getRowId={(row) => (isProcessingReferralRow(row) ? `processing-${row.id}` : row.id)}
      manipulationOptions={{
        currentPage: pagination.pageIndex,
        onPaginationChange,
        totalRowCount: totalCount,
        type: 'serverSide',
      }}
      pageSize={pagination.pageSize}
      renderSubRow={(row) =>
        isProcessingReferralRow(row) ? (
          <ProcessingReferralMessage row={row} />
        ) : (
          <SubmittedReferralInvites invites={row.invites} />
        )
      }
    />
  );
};

const SubmittedReferrals = () => {
  const { t } = useTranslation();
  const { error, fetching, pagination, refetch, sessions, setPagination, totalCount } = useSubmittedReferrals();
  const { cancelling, closeDialog, confirmCancel, openDialog, target } = useCancelReferralRequestDialog(refetch);
  const {
    error: processingError,
    processingReferralRequests,
    refetch: refetchProcessing,
  } = useProcessingReferralRequests();
  const refetchAll = useCallback(() => {
    refetch();
    refetchProcessing();
  }, [refetch, refetchProcessing]);
  const processingRows = useProcessingReferralRows({
    error: processingError,
    processingReferralRequests,
    refetch: refetchAll,
    sessions,
  });
  const rows = useMemo<SubmittedReferralRow[]>(
    () => (pagination.pageIndex === 0 ? [...processingRows, ...sessions] : sessions),
    [pagination.pageIndex, processingRows, sessions]
  );
  const columns = useTableColumns(openDialog);

  return (
    <>
      <Header className="mt-4" level="h2">
        {t('page.professionalReferral.home.submittedReferrals')}
      </Header>
      <SubmittedReferralsTable
        columns={columns}
        error={error}
        fetching={fetching}
        onPaginationChange={setPagination}
        pagination={pagination}
        rows={rows}
        totalCount={totalCount}
      />
      <CancelReferralRequestDialog
        cancelling={cancelling}
        onClose={closeDialog}
        onConfirm={confirmCancel}
        target={target}
      />
    </>
  );
};

export { SubmittedReferrals };
